/**
 * StartNowPage — /free-course/start-now?t=<token>
 *
 * An annual BKOS buyer whose free program has not unlocked yet (day 31) can
 * start it today by giving up the 30-day money-back refund
 * (worker/src/money-back-waiver.ts; thread mct_01fdf73c-3d2). The team sends
 * this link; the token carries no email. The words on the page come from the
 * server, so the text the buyer ticks is exactly the text stored as what they
 * agreed to. Opening the page records nothing: only the button press does.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const ACCENT = '#F05A28';
const MUTED = '#6b7280';

interface WaiverPage {
  state: 'ready' | 'open' | 'started' | 'unavailable';
  first_name: string | null;
  heading: string;
  intro: string | null;
  agreement: string | null;
  button: string;
  done: string;
  programs: Array<{ slug: string; label: string }>;
  started_program: string | null;
}

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; data: WaiverPage }
  | { phase: 'done'; message: string }
  | { phase: 'error'; message: string };

const button: React.CSSProperties = {
  padding: '12px 20px', borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
};

export default function StartNowPage() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`${API_BASE}/api/public/free-course/start-now?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!live) return;
        if (!r.ok) {
          setState({ phase: 'error', message: 'We could not find your free program from this link. Reply to the email it came in and we will sort it out.' });
          return;
        }
        setState({ phase: 'ready', data: (await r.json()) as WaiverPage });
      })
      .catch(() => live && setState({ phase: 'error', message: 'Something went wrong loading this page. Please try again in a moment.' }));
    return () => { live = false; };
  }, [token]);

  const start = async (d: WaiverPage, slug: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/free-course/start-now`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ t: token, program_slug: slug, agreed: d.state === 'ready' ? agreed : false }),
      });
      const data = (await r.json().catch(() => ({}))) as { error?: string; status?: string };
      if (!r.ok) { setError(data.error ?? 'That did not go through. Please try again.'); return; }
      setState({
        phase: 'done',
        message: data.status === 'pending_manual'
          ? 'You’re in. Our team is setting up your access now and will email you within one business day.'
          : d.done,
      });
    } catch {
      setError('That did not go through. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <TakerShell>
      <main style={{ maxWidth: 640, margin: '0 auto', padding: '48px 16px 72px' }} data-testid="start-now-page">
        {state.phase === 'loading' && <p style={{ color: MUTED }}>Loading…</p>}

        {state.phase === 'error' && (
          <>
            <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>Your free program</h1>
            <p role="alert">{state.message}</p>
          </>
        )}

        {state.phase === 'done' && (
          <div data-testid="start-now-done">
            <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>{state.message}</h1>
          </div>
        )}

        {state.phase === 'ready' && (() => {
          const d = state.data;
          if (d.state === 'started') {
            return (
              <div data-testid="start-now-started">
                <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>You’ve already started</h1>
                <p style={{ lineHeight: 1.6 }}>{d.started_program ?? 'Your free program'} is yours. Look for its welcome email, or reply to the email this link came in if you can’t find it.</p>
              </div>
            );
          }
          if (d.state === 'unavailable' || d.programs.length === 0) {
            return (
              <div data-testid="start-now-unavailable">
                <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>Your free program</h1>
                <p style={{ lineHeight: 1.6 }}>There’s nothing to start from this link right now. Reply to the email it came in and we’ll help.</p>
              </div>
            );
          }
          const program = d.programs[0];
          return (
            <>
              <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>{d.first_name ? `${d.first_name}, ${d.heading[0].toLowerCase()}${d.heading.slice(1)}` : d.heading}</h1>
              {d.state === 'open' ? (
                <p style={{ lineHeight: 1.6, color: '#334155', margin: '0 0 24px' }}>Your free program is open. There’s nothing to pay.</p>
              ) : (
                <>
                  <p style={{ lineHeight: 1.6, color: '#334155', margin: '0 0 20px' }}>{d.intro}</p>
                  <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '16px 18px', margin: '0 0 20px', lineHeight: 1.55, cursor: 'pointer' }}>
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 4, width: 18, height: 18, flexShrink: 0 }} data-testid="start-now-agree" />
                    <span style={{ fontWeight: 600 }}>{d.agreement}</span>
                  </label>
                </>
              )}
              <button type="button" style={{ ...button, opacity: d.state === 'ready' && !agreed ? 0.5 : 1 }}
                disabled={busy || (d.state === 'ready' && !agreed)} onClick={() => start(d, program.slug)} data-testid="start-now-button">
                {busy ? 'Setting it up…' : d.state === 'open' ? 'Start it now' : d.button}
              </button>
              {error && <p role="alert" style={{ color: '#B91C1C', fontSize: 14 }}>{error}</p>}
              <p style={{ color: MUTED, fontSize: 14, marginTop: 20 }}>Reply to the email this link came in if you need a hand.</p>
            </>
          );
        })()}
      </main>
    </TakerShell>
  );
}
